/**
 * Page Transition Wrapper
 *
 * Provides subtle fade + slide transitions on route changes.
 * Wraps page content with AnimatePresence-driven motion.
 * Uses shared animation tokens from @cgraph-dev/animation-constants.
 *
 */

import { motion, useReducedMotion } from 'motion/react';
import { useLocation } from 'react-router-dom';
import { transitions } from '@cgraph-dev/animation-constants';

const PAGE_VARIANTS = {
  initial: transitions.pageEnter.initial,
  animate: transitions.pageEnter.animate,
  exit: transitions.pageEnter.exit,
};

const PAGE_SPRING_TRANSITION = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

const INSTANT_TRANSITION = { duration: 0 };

interface PageTransitionProps {
  readonly children: React.ReactNode;
}

/**
 * Wraps its children with a route-keyed Framer Motion transition.
 * Uses pathname as the motion key for clean re-renders on navigation.
 */
export function PageTransition({ children }: PageTransitionProps): React.ReactElement {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      key={location.pathname}
      variants={PAGE_VARIANTS}
      initial={prefersReducedMotion ? false : 'initial'}
      animate="animate"
      exit="exit"
      transition={prefersReducedMotion ? INSTANT_TRANSITION : PAGE_SPRING_TRANSITION}
    >
      {children}
    </motion.div>
  );
}
